import { z } from 'zod';

// ─── AI-generated question validation ──────────────
export const TriviaQuestionSchema = z.object({
  id: z.string().min(1),
  realm: z.string().min(1),
  difficulty: z.enum(['easy', 'medium', 'hard']),
  question: z.string().min(10).max(500),
  imagePrompt: z.string().max(1000).optional(),
  choices: z.tuple([
    z.string().min(1).max(200),
    z.string().min(1).max(200),
    z.string().min(1).max(200),
    z.string().min(1).max(200),
  ]),
  correctIndex: z.number().int().min(0).max(3),
  explanation: z.string().min(1).max(500),
  language: z.string().default('en'),
  safetyFlag: z.boolean().default(false),
});

export const TriviaQuestionArraySchema = z.array(TriviaQuestionSchema);

// ─── Client payloads ──────────────────────────────
export const NicknameSchema = z
  .string()
  .min(2, 'Nickname must be at least 2 characters')
  .max(20, 'Nickname must be at most 20 characters')
  .regex(/^[a-zA-Z0-9_\- ]+$/, 'Only letters, numbers, spaces, hyphens, and underscores');

export const JoinRoomSchema = z.object({
  nickname: NicknameSchema,
  inviteCode: z.string().length(6, 'Invite code must be 6 characters'),
});

export const CreateRoomSchema = z.object({
  nickname: NicknameSchema,
});

export const SubmitAnswerSchema = z.object({
  questionId: z.string().min(1),
  choiceIndex: z.number().int().min(0).max(3),
  timestamp: z.number().positive(),
});

export const MatchmakingJoinSchema = z.object({
  nickname: NicknameSchema,
});

// ─── AI provider raw response ─────────────────────
export const AIRawQuestionSchema = z.object({
  realm: z.string(),
  difficulty: z.enum(['easy', 'medium', 'hard']),
  question: z.string(),
  imagePrompt: z.string().optional(),
  choices: z.array(z.string()).length(4),
  correctIndex: z.number().int().min(0).max(3),
  explanation: z.string(),
  language: z.string().optional().default('en'),
  safetyFlag: z.boolean().optional().default(false),
});

export const AIRawResponseSchema = z.object({
  questions: z.array(AIRawQuestionSchema),
});

// ─── Type exports from schemas ────────────────────
export type TriviaQuestionInput = z.infer<typeof TriviaQuestionSchema>;
export type SubmitAnswerInput = z.infer<typeof SubmitAnswerSchema>;
export type JoinRoomInput = z.infer<typeof JoinRoomSchema>;
export type CreateRoomInput = z.infer<typeof CreateRoomSchema>;
export type AIRawQuestion = z.infer<typeof AIRawQuestionSchema>;
